import React, { FC, HTMLProps, useMemo } from "react";
import { handleGridStyles } from "./generateStyle";
import { AppConfig } from "../../config";
import cn from "classnames";
import { isPresent } from "../../utils";

handleGridStyles(
  AppConfig.responsive.breakpoints,
  AppConfig.responsive.breakpointsExtension
);

const { breakpoints } = AppConfig.responsive;
const breakpointsNames = breakpoints.map(({ name }) => name);

type ElementType<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<
  infer ElementType
>
  ? ElementType
  : never;

export type GridBreakpointsNames = ElementType<typeof breakpointsNames>;
export type GridBreakpointsSizes =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12;
export type GridBreakpoints = {
  [k in GridBreakpointsNames]?: GridBreakpointsSizes;
};
export type GridGutter = [number, number] | number | undefined;

type Props = HTMLProps<HTMLDivElement> &
  GridBreakpoints & {
    item?: boolean;
    container?: boolean;
    gutter?: GridGutter;
    offset?: number; // TODO: to do :D
    order?:
      | "first"
      | "last"
      | {
          [k in GridBreakpointsNames]?:
            | 1
            | 2
            | 3
            | 4
            | 5
            | 6
            | 7
            | 8
            | 9
            | 10
            | 11
            | 12;
        };
  };

type GridGutterNormalized = {
  horizontal: number | null;
  vertical: number | null;
};
const normalizeGutter = (gutter: GridGutter): GridGutterNormalized => {
  const res: GridGutterNormalized = {
    horizontal: null,
    vertical: null,
  };

  if (!isPresent(gutter)) return res;

  if (Array.isArray(gutter)) {
    res.horizontal = gutter[0];
    res.vertical = gutter[1];
  } else {
    res.horizontal = gutter;
  }

  return res;
};

const Grid: FC<Props> = ({
  children,
  container,
  item,
  gutter,
  order,
  ...props
}) => {
  const columnClassNames = breakpointsNames.reduce((acc, name) => {
    const val = props[name];
    if (val) return acc + ` col-${name}-${val}`;
    return acc;
  }, "col");

  const isChildrenPresent = React.Children.toArray(children).length > 0;

  const containerGutterStyles = useMemo(() => {
    const style: { [k: string]: string } = {};
    const gutterNormalized = normalizeGutter(gutter);
    if (isChildrenPresent) {
      if (isPresent(gutterNormalized.horizontal)) {
        style["--grid-gutter-lr"] = gutterNormalized.horizontal + "px";
      }
      if (isPresent(gutterNormalized.vertical)) {
        style["--grid-gutter-tb"] = gutterNormalized.vertical + "px";
      }
    }
    return style;
  }, [gutter, isChildrenPresent]);

  const orderClassNames = useMemo<string>(() => {
    if (typeof order === "object") {
      return Object.entries(order).reduce((acc, [key, num]) => {
        return acc + ` order-${key}-${num}`;
      }, "");
    }
    return "";
  }, [order]);

  return (
    <div
      {...props}
      style={{ ...props.style, ...containerGutterStyles }}
      className={cn(
        props.className,
        {
          row: container,
        },
        item ? columnClassNames : null,
        orderClassNames
      )}
    >
      {children}
    </div>
  );
};

export default Grid;
